import {IResourceComponentsProps, useNavigation, useShow, useTranslate, useNotification} from "@refinedev/core";
import {
    Card,
    CardContent,
    CardHeader,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Typography,
    useMediaQuery,
    useTheme,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
    Chip,
} from "@mui/material";
import React, {useEffect} from "react";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import { MapContainer, TileLayer, Marker, Popup} from 'react-leaflet';
import L from 'leaflet';
import Avatar from "@mui/material/Avatar";
import { Phone } from "lucide-react";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {getDuration} from "../../utils/getDuration";
import { useGeocoding } from "../../hooks/useGeocoding";
import { IVSPlayer } from "../../components/IVSPlayer";
import { getRecordingUrl } from "../../api/videos";
import axiosInstance from "../../api/axios-config";




export const AlertHistoryShow: React.FC<IResourceComponentsProps> = () => {


    const translate = useTranslate();
    const { queryResult } = useShow({
        meta: {
            join: [
                { field: "alert" },
                { field: "user" },
                { field: "user.subGroup" }
            ]
        }
    });
    const { data, isLoading } = queryResult;
    const theme = useTheme();
    const { goBack } = useNavigation();
    const isSmallOrLess = useMediaQuery(theme.breakpoints.down("sm"));
    const record = data?.data;

    // Reverse geocoding pour obtenir l'adresse
    const { address, loading: addressLoading } = useGeocoding(
        record?.latitude,
        record?.longitude,
        { priority: 1 }
    );
    const [duration, setDuration] = React.useState('0');
    const { open: openNotification } = useNotification();

    // États pour le replay
    const [replayUrl, setReplayUrl] = React.useState<string | null>(null);
    const [isLoadingReplay, setIsLoadingReplay] = React.useState(false);
    const [replayError, setReplayError] = React.useState<string | null>(null);
    const [playerConnected, setPlayerConnected] = React.useState(false);
    const [replayAttempted, setReplayAttempted] = React.useState(false);

    // État pour le status de l'alerte (directement sur EmergencyBroadcast)
    const [broadcastStatus, setBroadcastStatus] = React.useState<'NEW' | 'ACCEPTED' | 'CLOSED' | 'CALLED'>('NEW');
    const [isUpdatingStatus, setIsUpdatingStatus] = React.useState(false);

    useEffect(() => {
        if (record?.createdAt && record?.updatedAt) {
            const calculateDuration = getDuration({
                createdAt: record?.createdAt,
                endedAt:  record?.updatedAt,
            });
            setDuration(calculateDuration);
        }
    }, [record]);

    // Synchroniser le status depuis le record (EmergencyBroadcast a directement un champ status)
    useEffect(() => {
        if (record?.status) {
            setBroadcastStatus(record.status);
        }
    }, [record?.status]);

    // Réinitialiser les états du replay quand le streamId change
    useEffect(() => {
        setReplayUrl(null);
        setReplayError(null);
        setReplayAttempted(false);
        setPlayerConnected(false);
    }, [record?.streamId]);

    // Fonction pour mettre à jour le status du broadcast
    const updateBroadcastStatus = React.useCallback(async (newStatus: 'NEW' | 'ACCEPTED' | 'CLOSED' | 'CALLED') => {
        if (!record?.id || isUpdatingStatus) {
            return;
        }

        setIsUpdatingStatus(true);
        try {
            await axiosInstance.put(`/alert/${record.id}/status`, {
                status: newStatus
            });

            setBroadcastStatus(newStatus);

            openNotification?.({
                type: "success",
                message: translate("monitoring.statusUpdated"),
                description: translate("monitoring.statusUpdateSuccess"),
            });

            if (queryResult.refetch) {
                queryResult.refetch();
            }
        } catch (error) {
            openNotification?.({
                type: "error",
                message: translate("monitoring.statusUpdateError"),
                description: translate("monitoring.statusUpdateErrorMessage"),
            });
        } finally {
            setIsUpdatingStatus(false);
        }
    }, [record?.id, isUpdatingStatus, queryResult, openNotification, translate]);

    // Récupérer le replay automatiquement quand on accède à un broadcast terminé
    useEffect(() => {
        const fetchReplay = async () => {
            const streamId = record?.streamId;

            // Éviter la boucle infinie : ne pas réessayer si on a déjà fait une tentative
            if (isLoadingReplay || replayUrl || replayAttempted) {
                return;
            }

            setIsLoadingReplay(true);
            setReplayError(null);
            setReplayAttempted(true);

            try {
                // Chercher l'URL de replay via l'API (enregistrement S3/CloudFront)
                if (streamId) {
                    console.log('[AlertHistory] Récupération du replay pour streamId:', streamId);
                    const url = await getRecordingUrl(streamId);

                    if (url) {
                        console.log('[AlertHistory] URL de replay trouvée:', url);
                        setReplayUrl(url);
                    } else {
                        setReplayError("Le replay n'est pas disponible pour cet enregistrement.");
                    }
                } else {
                    setReplayError("Aucune information de replay disponible.");
                }
            } catch (error) {
                setReplayError("Erreur lors du chargement du replay");
            } finally {
                setIsLoadingReplay(false);
            }
        };

        if (record && record?.isBroadcasting === false) {
            fetchReplay();
        }
    }, [record, isLoadingReplay, replayUrl, replayAttempted]);

    // Callbacks du player
    const handlePlayerLoad = React.useCallback(() => {
        setPlayerConnected(true);
    }, []);

    const handlePlayerError = React.useCallback((error: string) => {
        setPlayerConnected(false);
    }, []);

    const handlePlayerStateChange = React.useCallback((state: string) => {
        if (state === 'Playing') {
            setPlayerConnected(true);
        }
    }, []);



    const greenIcon = L.icon({
        iconUrl: 'https://res.cloudinary.com/dlnimaboq/image/upload/v1702476006/oupbiebxhfkbc49lckoj.png',
        iconSize:     [48, 48], // size of the icon
        iconAnchor:   [24, 24], // point of the icon which will correspond to marker's location
        shadowAnchor: [4, 62],  // the same for the shadow
        popupAnchor:  [0, -20] // point from which the popup should open relative to the iconAnchor
    });


    // Afficher un loader pendant le chargement initial pour éviter le flash
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "grid",
          width: "100%",
          height: "100%",
          gridTemplateColumns: {
            xs: "1fr", // 1 column on extra small screens
            sm: "1fr", // 1 column on small screens
            md: "minmax(300px, 28%) 1fr", // Colonne gauche légèrement réduite, droite prend le reste
          },
          gap: 2,
        }}
      >
        <Card>
          <CardContent>
            <Box sx={{
              display: "flex",
              flexDirection: { xs: "column", xl: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", xl: "center" },
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: { xs: 0, lg: 2 } }}>
                <IconButton onClick={goBack}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">
                  {translate("alert.fields.alertId")}
                </Typography>
                <Typography variant="caption">{`#${
                  record?.id ?? ""
                }`}</Typography>
              </Box>

              <Box sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                mb: 2,
                width: { xs: "100%", lg: "auto" }
              }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={broadcastStatus}
                    onChange={(e) => updateBroadcastStatus(e.target.value as any)}
                    disabled={isUpdatingStatus}
                    size="small"
                  >
                    <MenuItem value="NEW">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip label={translate("monitoring.status.new")} color="info" size="small" />
                      </Stack>
                    </MenuItem>
                    <MenuItem value="ACCEPTED">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip label={translate("monitoring.status.accepted")} color="warning" size="small" />
                      </Stack>
                    </MenuItem>
                    <MenuItem value="CALLED">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip label={translate("monitoring.status.called")} color="secondary" size="small" />
                      </Stack>
                    </MenuItem>
                    <MenuItem value="CLOSED">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip label={translate("monitoring.status.closed")} color="success" size="small" />
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Section Replay */}
            <Box
              sx={{
                width: "100%",
                paddingTop: "177.77%", // 9:16 aspect ratio
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              >
                {isLoadingReplay ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : "#f5f5f5",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "10px",
                    }}
                  >
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Chargement du replay...
                    </Typography>
                  </Box>
                ) : replayUrl ? (
                  <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    <IVSPlayer
                      key={`replay-${replayUrl}`}
                      streamUrl={replayUrl}
                      isLive={false}
                      controls={true}
                      autoplay={false}
                      onPlayerLoad={handlePlayerLoad}
                      onPlayerError={handlePlayerError}
                      onPlayerStateChange={handlePlayerStateChange}
                      style={{
                        width: "100%",
                        height: "100%",
                      }}
                    />
                    {/* Indicateur de replay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        zIndex: 10,
                      }}
                    >
                      📹 REPLAY
                    </Box>
                    {/* Message "Cliquez pour lire" au centre */}
                    {!playerConnected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          px: 3,
                          py: 2,
                          borderRadius: 2,
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          zIndex: 10,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                        }}
                      >
                        ▶️ Cliquez pour lire le replay
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : "#f5f5f5",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "10px",
                      p: 2,
                    }}
                  >
                    <Typography variant="h5" color="text.primary" sx={{ mb: 2 }}>
                      Stream terminé
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      textAlign="center"
                      sx={{ mb: 2 }}
                    >
                      {replayError || "Le replay n'est pas disponible pour cet enregistrement."}
                    </Typography>
                    {duration && duration !== "0" && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Durée : {duration}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Stack
          spacing={2}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          <Card sx={{ flexShrink: 0 }}>
            <CardContent>
              <Stepper
                nonLinear
                activeStep={record?.alert[0]?.isBroadcasting ? 3 : 4}
                orientation={isSmallOrLess ? "vertical" : "horizontal"}
              >
                <Step key={1}>
                  {/* <StepLabel
                    optional={
                      <Typography variant="caption">
                        {record?.createdAt &&
                          dayjs(record?.createdAt).format("L LT")}
                      </Typography>
                    }
                    error={record?.createdAt === ""}
                  >
                    {translate("alert.fields.tracking-created")}
                  </StepLabel>
                </Step>
                <Step key={2}> */}
                  <StepLabel
                    optional={
                      <Typography variant="caption">
                        {record?.createdAt &&
                          dayjs(record?.createdAt).format("L LT")}
                      </Typography>
                    }
                    error={record?.createdAt === ""}
                  >
                    {translate("alert.fields.alarm-started")}
                  </StepLabel>
                </Step>
                <Step key={2}>
                  <StepLabel
                    optional={
                      <Typography variant="caption">
                        {record?.alert[0]?.isBroadcasting
                          ? translate("alert.fields.is-broadcasting")
                          : translate("alert.fields.duree") + " " + duration}
                      </Typography>
                    }
                    error={record?.alert[0]?.isBroadcasting === null}
                  >
                    {translate("alert.fields.is-broadcasting")}
                  </StepLabel>
                </Step>
                <Step key={3}>
                  <StepLabel
                    optional={
                      <Typography variant="caption">
                        {!record?.alert[0]?.isBroadcasting && record?.updatedAt &&
                          dayjs(record?.updatedAt).format("L LT")}
                      </Typography>
                    }
                    error={record?.updatedAt === ""}
                  >
                    {translate("alert.fields.tracking-ended")}
                  </StepLabel>
                </Step>
              </Stepper>
            </CardContent>
          </Card>

          <Paper sx={{ padding: 2, flexShrink: 0 }}>
            <Stack
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              justifyContent={isSmallOrLess ? "center" : "space-between"}
            >
              <Stack
                direction={isSmallOrLess ? "column" : "row"}
                alignItems={"center"}
                textAlign={isSmallOrLess ? "center" : "left"}
                gap={2}
              >
                <Avatar
                  alt={record?.user?.fullname}
                  src={record?.user?.profilePicture}
                  sx={{ width: 100, height: 100 }}
                />
                <Box>
                  <Typography>{translate("monitoring.user")}</Typography>
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="h6">{record?.user?.fullname || record?.user?.email}</Typography>
                    {record?.user?.subGroup?.name && (
                      <Chip
                        icon={<AccountTreeIcon fontSize="small" />}
                        label={record.user.subGroup.name}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: '16px',
                          '& .MuiChip-icon': {
                            color: 'primary.main',
                          },
                        }}
                      />
                    )}
                  </Stack>
                  {record?.latitude && record?.longitude && !addressLoading && address && (
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {address}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>
              <Stack
                direction="row"
                gap={2}
                flexWrap="wrap"
                justifyContent="center"
                alignItems="center"
                sx={{
                  p: 2,
                  backgroundColor: "#36B37E",
                  borderRadius: 2,
                  color: "white",
                }}
              >
                <Phone size={36} />
                <div>
                  <Typography>{translate("monitoring.phoneNumber")}</Typography>
                  <Typography variant="h6">{record?.user?.phone}</Typography>
                </div>
              </Stack>
            </Stack>
          </Paper>

          <Box sx={{ flex: 1, minHeight: 0, height: '100%' }}>
            {record?.latitude && record?.longitude && (
              <MapContainer
                center={[record?.latitude, record?.longitude]}
                zoom={20}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                    attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url={`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${process.env.REACT_APP_STADIA_API_KEY}`}
                    minZoom={0}
                    maxZoom={20}
                />


                <Marker
                  icon={greenIcon}
                  position={[record?.latitude, record?.longitude]}
                >
                  <Popup>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                      <a
                        href={`https://www.google.com/maps?q=${record?.latitude},${record?.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#1976d2',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          textAlign: 'center',
                          transition: 'background-color 0.2s'
                        }}
                      >
                        📍 Ouvrir dans Google Maps
                      </a>
                      <a
                        href={`https://www.google.com/maps?q=&layer=c&cbll=${record?.latitude},${record?.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#1976d2',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          textAlign: 'center',
                          transition: 'background-color 0.2s'
                        }}
                      >
                        🗺️ Ouvrir dans Street View
                      </a>
                    </Box>
                  </Popup>
                </Marker>
              </MapContainer>
            )}
          </Box>
        </Stack>
      </Box>
    );
};
